import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Radio,
  RadioGroup,
} from '@fluentui/react-components';
import { DismissRegular, LinkRegular } from '@fluentui/react-icons';
import { TenderingFeeMeasure, TenderingPart } from '../../../models/tendering';
import { deepCloneObject } from '../../../utils/clone';
import { getQuestionHeader } from '../../../utils/tendering-utils';
import { uuidv4 } from '../../../utils/uuid';
import QuestionDialog from '../../dialogs/QuestionDialog';

interface ContractFeeMeasureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (measure: TenderingFeeMeasure) => void;
  tenderingParts: TenderingPart[];
  measure?: TenderingFeeMeasure;
}

const ContractFeeMeasureModal: React.FC<ContractFeeMeasureModalProps> = ({
  isOpen,
  onClose,
  onSave,
  tenderingParts,
  measure: initialMeasure,
}) => {
  const [measure, setMeasure] = useState<TenderingFeeMeasure | null>(null);
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [edit, setEdit] = useState(false);

  const feeValueTypes = ['Timmar', 'Dygn', 'Månad', 'Antal'];
  const feeTypes = ['Procent', 'Fastpris'];

  useEffect(() => {
    if (isOpen) {
      if (initialMeasure) {
        setMeasure(deepCloneObject(initialMeasure));
        setEdit(true);
      } else {
        setMeasure({
          id: uuidv4(),
          header: '',
          valueType: feeValueTypes[0],
          feeType: feeTypes[0],
          feeValue: 0,
          valueQuestion: undefined,
          allowEmptyReport: true,
        });
        setEdit(false);
      }
    }
  }, [isOpen, initialMeasure]);

  const updateMeasure = (updates: Partial<TenderingFeeMeasure>) => {
    setMeasure((prev) => ({ ...prev!, ...updates }));
  };

  const handleSave = () => {
    if (measure) {
      onSave(measure);
    }
  };

  const handleQuestionSelect = (data: any) => {
    if (data && data.selected && data.selected.length > 0) {
      updateMeasure({ valueQuestion: data.selected[0].questionID });
    }
    setQuestionModalOpen(false);
  };

  const getQuestionHeaderText = (id?: number): string => {
    if (!id) return '';
    return getQuestionHeader(id, tenderingParts);
  };

  if (!measure) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
        <DialogSurface className="max-w-[500px]">
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="close"
                  icon={<DismissRegular />}
                  onClick={onClose}
                />
              }
            >
              {edit ? 'Redigera' : 'Skapa'} mätpunkt
            </DialogTitle>
            <DialogContent className="space-y-4">
              {/* Rubrik */}
              <div>
                <h5 className="text-sm font-semibold mb-2">Rubrik</h5>
                <Input
                  value={measure.header}
                  onChange={(e) => updateMeasure({ header: e.target.value })}
                  className="w-full"
                />
              </div>

              {/* Värdetype */}
              <div>
                <h5 className="text-sm font-semibold mb-2">Värdetype</h5>
                <RadioGroup
                  value={measure.valueType}
                  onChange={(_, data) => updateMeasure({ valueType: data.value })}
                >
                  {feeValueTypes.map((type) => (
                    <Radio key={type} value={type} label={type} />
                  ))}
                </RadioGroup>
              </div>

              {/* Typ av andel */}
              <div>
                <h5 className="text-sm font-semibold mb-2">Typ av andel</h5>
                <RadioGroup
                  value={measure.feeType}
                  onChange={(_, data) => updateMeasure({ feeType: data.value })}
                >
                  {feeTypes.map((type) => (
                    <Radio key={type} value={type} label={type} />
                  ))}
                </RadioGroup>
              </div>

              {/* Andel per värdedel */}
              <div>
                <h5 className="text-sm font-semibold mb-2">Andel per värdedel</h5>
                <Input
                  type="number"
                  value={measure.feeValue?.toString() || '0'}
                  onChange={(e) => updateMeasure({ feeValue: Number(e.target.value) })}
                  className="w-full"
                  step="0.000001"
                />
              </div>

              {/* Kopplad värdemängd */}
              <div>
                <h5 className="text-sm font-semibold mb-2">
                  Kopplad värdemängd (Kostnad per värdetype)
                </h5>
                {measure.feeType !== 'Fastpris' ? (
                  <div className="flex items-center gap-3 p-3 border border-gray-300 rounded">
                    <Button
                      appearance="subtle"
                      icon={<LinkRegular />}
                      onClick={() => setQuestionModalOpen(true)}
                      title="Koppla värdemängd"
                    />
                    <p className="text-sm italic">
                      {getQuestionHeaderText(measure.valueQuestion) || 'Ingen fråga kopplad'}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm italic text-gray-500">Ej relevant</p>
                )}
              </div>

              {/* Tillåt tom rapport */}
              <div>
                <h5 className="text-sm font-semibold mb-2">Tillåt tom rapport</h5>
                <RadioGroup
                  value={measure.allowEmptyReport ? 'true' : 'false'}
                  onChange={(_, data) =>
                    updateMeasure({ allowEmptyReport: data.value === 'true' })
                  }
                >
                  <Radio value="true" label="Ja" />
                  <Radio value="false" label="Nej" />
                </RadioGroup>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={onClose}>
                Avbryt
              </Button>
              <Button appearance="primary" onClick={handleSave}>
                {edit ? 'Redigera mätpunkt' : 'Lägg till mätpunkt'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Question Modal */}
      {questionModalOpen && (
        <QuestionDialog
          isOpen={questionModalOpen}
          onClose={() => setQuestionModalOpen(false)}
          onSelect={handleQuestionSelect}
          tenderingParts={tenderingParts}
          singleSelect={true}
          selectedQuestions={
            measure.valueQuestion ? [{ questionID: measure.valueQuestion }] : undefined
          }
        />
      )}
    </>
  );
};

export default ContractFeeMeasureModal;
